/**
 * Beep corto y seco, tipo lector industrial, para confirmar un escaneo.
 *
 * Se sintetiza con Web Audio: no hay archivo de sonido que descargar ni cachear.
 * Suena en mobile cuando se escanea SIN foco (Enter del lector via
 * ScanTargetContext o lectura de la camara), sin importar si despues la busqueda
 * encuentra algo. Con un campo enfocado no suena. Se apaga desde el perfil
 * (SONIDO DE ESCANEO): los llamadores chequean `isScanBeepEnabled` de UIContext.
 *
 * Los navegadores bloquean el audio hasta la primera interaccion del usuario.
 * `unlockScanBeep` se llama en el primer toque para dejar el contexto listo; el
 * Enter del lector y la lectura de la camara ya vienen de una interaccion, asi
 * que tambien lo destraban.
 */

let audioCtx: AudioContext | null = null

const getContext = (): AudioContext | null => {
  if (audioCtx) return audioCtx
  const AudioContextClass =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!AudioContextClass) return null
  audioCtx = new AudioContextClass({ latencyHint: 'interactive' })
  return audioCtx
}

/** Deja el contexto de audio corriendo. Seguro de llamar muchas veces. */
export const unlockScanBeep = () => {
  const ac = getContext()
  if (ac && ac.state !== 'running') ac.resume().catch(() => {})
}

const playBeep = (ac: AudioContext) => {
  // Separacion minima: el primer beep tras el resume puede perderse si la
  // salida de audio inicializa tarde.
  const start = ac.currentTime + 0.025
  const duration = 0.072

  const master = ac.createGain()
  const compressor = ac.createDynamicsCompressor()

  compressor.threshold.setValueAtTime(-18, start)
  compressor.knee.setValueAtTime(8, start)
  compressor.ratio.setValueAtTime(7, start)
  compressor.attack.setValueAtTime(0.001, start)
  compressor.release.setValueAtTime(0.04, start)

  master.gain.setValueAtTime(0.0001, start)
  master.gain.exponentialRampToValueAtTime(0.16, start + 0.0025)
  master.gain.setValueAtTime(0.145, start + 0.048)
  master.gain.exponentialRampToValueAtTime(0.0001, start + duration)

  master.connect(compressor)
  compressor.connect(ac.destination)

  // Fundamental: tono alto y seco de buzzer piezoelectrico.
  const fundamental = ac.createOscillator()
  fundamental.type = 'square'
  fundamental.frequency.setValueAtTime(2670, start)
  fundamental.frequency.exponentialRampToValueAtTime(2580, start + duration)
  const g1 = ac.createGain()
  g1.gain.setValueAtTime(0.7, start)
  fundamental.connect(g1)
  g1.connect(master)

  // Armonico superior: el caracter metalico del piezo.
  const harmonic = ac.createOscillator()
  harmonic.type = 'sine'
  harmonic.frequency.setValueAtTime(5340, start)
  const g2 = ac.createGain()
  g2.gain.setValueAtTime(0.13, start)
  harmonic.connect(g2)
  g2.connect(master)

  // Transitorio inicial, como el ataque del buzzer fisico.
  const buffer = ac.createBuffer(1, Math.floor(ac.sampleRate * 0.008), ac.sampleRate)
  const data = buffer.getChannelData(0)
  for (let i = 0; i < data.length; i++) {
    const env = 1 - i / data.length
    data[i] = (Math.random() * 2 - 1) * env * 0.16
  }
  const noise = ac.createBufferSource()
  noise.buffer = buffer
  const hp = ac.createBiquadFilter()
  hp.type = 'highpass'
  hp.frequency.setValueAtTime(1800, start)
  const ng = ac.createGain()
  ng.gain.setValueAtTime(0.1, start)
  noise.connect(hp)
  hp.connect(ng)
  ng.connect(master)

  fundamental.start(start)
  harmonic.start(start)
  noise.start(start)

  fundamental.stop(start + duration + 0.01)
  harmonic.stop(start + duration + 0.01)
  noise.stop(start + 0.008)
}

/** Reproduce el beep de escaneo. Nunca lanza: el sonido es accesorio. */
export const playScanBeep = () => {
  try {
    const ac = getContext()
    if (!ac) return
    if (ac.state === 'running') {
      playBeep(ac)
      return
    }
    ac.resume()
      .then(() => playBeep(ac))
      .catch(() => {})
  } catch {
    // Sin soporte de audio: el escaneo sigue igual.
  }
}
